/**
 * Danish first (15/10/2026): a country minute (or an instant) on the Danish
 * clock, exact for the run date — the same `localToDk` as the planner, never
 * today's offsets applied to another day. "−1d" = the Danish date is the day
 * before the country's (Australia's morning is Denmark's evening).
 * For Media Devoted | Created By Ciro Autiero
 */
import { localDateInTz, localToDk } from '@trafficpulse/shared';

import { minutesLabel, minutesToHHMM } from '../format';

export interface ClockCtx {
  tz: string | null;
  flag: string;
  runDate?: string | null;
}

export const shiftMark = (shift: number): string => (shift < 0 ? ' −1d' : shift > 0 ? ' +1d' : '');

/** The Danish clock time of a country minute on the run day; null without a timezone. */
export function dkTime(localMin: number, tz: string | null, runDate?: string | null): { label: string; shift: number } | null {
  if (!tz) return null;
  const { dkMin, dayShift } = localToDk(localMin, { tz, runDate: runDate ?? localDateInTz(new Date(), tz) });
  return { label: minutesToHHMM(dkMin), shift: dayShift };
}

export function dkLabel(localMin: number, tz: string | null, runDate?: string | null): string | null {
  const t = dkTime(localMin, tz, runDate);
  return t ? `${t.label}${shiftMark(t.shift)}` : null;
}

/** "05:00 🇩🇰 · 🇦🇺 14:00" — Danish first; the country's alone without a timezone. */
export function clockText(localMin: number, c: ClockCtx): string {
  const dk = dkLabel(localMin, c.tz, c.runDate);
  return dk ? `${dk} 🇩🇰 · ${c.flag} ${minutesLabel(localMin)}` : minutesLabel(localMin);
}

/** "05:00 → 06:45 🇩🇰 · 🇦🇺 14:00 → 15:45". */
export function rangeText(fromMin: number, toMin: number, c: ClockCtx): string {
  const a = dkLabel(fromMin, c.tz, c.runDate);
  const b = dkLabel(toMin, c.tz, c.runDate);
  const local = `${minutesLabel(fromMin)} → ${minutesLabel(toMin)}`;
  return a && b ? `${a} → ${b} 🇩🇰 · ${c.flag} ${local}` : local;
}

const clockOf = (iso: string, timeZone: string) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone });

/** An instant on the Danish clock, then the country's: "09:00 🇩🇰 · 🇦🇺 18:00". */
export function instantText(iso: string, c: Pick<ClockCtx, 'tz' | 'flag'>): string {
  const dk = `${clockOf(iso, 'Europe/Copenhagen')} 🇩🇰`;
  return c.tz ? `${dk} · ${c.flag} ${clockOf(iso, c.tz)}` : dk;
}

/** The country's window, Danish first: "15:00 −1d – 07:00 🇩🇰 · 🇦🇺 00:00–16:00" (the window stays the
 *  country's rule; the mark says Australia's day opens the evening before in Denmark — QA 18/10/2026). */
export function windowText(startMin: number, endMin: number, c: ClockCtx): string {
  const local = `${minutesToHHMM(startMin)}–${minutesToHHMM(endMin)}`;
  const a = dkTime(startMin, c.tz, c.runDate);
  const b = dkTime(endMin, c.tz, c.runDate);
  if (!a || !b) return local;
  const dash = a.shift !== 0 || b.shift !== 0 ? ' – ' : '–';
  return `${a.label}${shiftMark(a.shift)}${dash}${b.label}${shiftMark(b.shift)} 🇩🇰 · ${c.flag} ${local}`;
}

/** An instant on the Danish clock with its Danish date — for a day marker against the country's date. */
export function instantDk(iso: string): { label: string; date: string } {
  const at = new Date(iso);
  return { label: clockOf(iso, 'Europe/Copenhagen'), date: at.toLocaleDateString('en-CA', { timeZone: 'Europe/Copenhagen' }) };
}

/** " −1d" / " +1d" when the Danish date is before / after the country's date. */
export const dateMark = (dkDate: string, countryDate: string): string => (dkDate < countryDate ? ' −1d' : dkDate > countryDate ? ' +1d' : '');
